<template>
  <v-card
    v-if="visible"
    class="attendance-log-editor-bar fill-height right-bar-overlay"
    data-testid="attendance-log-editor-bar"
    border
  >
    <v-window
      v-model="visible"
      :mandatory="false"
      eager
    >
      <v-window-item
        key="attendanceLog"
        value="attendanceLog"
      >
        <v-card
          class="fill-height"
          elevation="2"
          tile
        >
          <v-card-title class="d-flex bb-1 bc-very-light-grey fw-600 pa-5">
            <span tabindex="0">
              {{ $bct(`Edit ${terms.attendance}`) }}
            </span>
            <v-spacer />

            <v-btn
              @click="close"
              aria-label="Close edit attendance"
              class="focus-visible"
              icon="close"
              size="small"
              variant="text"
            />
          </v-card-title>

          <v-card class="bg-very-light-grey ma-3">
            <v-card-text>
              <p class="fs-16 c-grey-70">
                {{ attendanceLog.name }}
              </p>
              <p
                :data-testId="`attendance-log-v2-student-details-${attendanceLog.id}`"
                class="fs-12 c-grey-70"
              >
                ID {{ attendanceLog?.meta?.child?.external_id }} | DOB
                {{ attendanceLog?.meta?.child?.dob }}
              </p>
              <p class="fs-12 c-grey-70">
                {{ `${$bct('Month')}: ${getMonthText(attendanceLog.month, attendanceLog.year)}` }}
              </p>
            </v-card-text>
          </v-card>
          <template v-if="attendanceLog">
            <v-card-text
              v-for="question in questions"
              :key="`${question.id}-${attendanceLog.id}`"
            >
              <QuestionWithField
                v-model="attendanceLog"
                :dense="true"
                :elevation="0"
                :hide-actions="true"
                :outlined="false"
                :paddingless="true"
                :processing="processing"
                :question="question"
                :readonly="readonly"
                :schema="schema?.definition"
                :very-dense="true"
              />
            </v-card-text>
          </template>
          <div class="d-flex justify-space-between pa-4">
            <div>
              <v-btn
                v-if="canUnsubmit"
                @click="handleUnsubmit"
                :loading="processing"
                :ripple="false"
                class="focus-very-visible"
                color="primary"
                data-testid="unsubmit-button"
                variant="outlined"
              >
                {{ $bct('Unsubmit') }}
              </v-btn>
            </div>
            <div>
              <v-btn
                @click="close"
                class="focus-very-visible mr-2"
                variant="outlined"
              >
                {{ $bct('Close') }}
              </v-btn>
              <v-btn
                v-if="!readonly"
                @click="handleSaveDraft"
                :loading="processing"
                :ripple="false"
                class="focus-very-visible mr-2"
                color="primary"
                data-testid="save-draft-button"
                variant="outlined"
              >
                {{ $bct('Save Draft') }}
              </v-btn>
              <v-btn
                v-if="submitButtonVisible"
                @click="handleSubmit"
                :disabled="readonly"
                :loading="processing"
                :ripple="false"
                class="focus-very-visible"
                color="primary"
                data-testid="submit-button"
              >
                {{ readonly ? $bct('Submitted') : $bct('Submit') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script setup>
import { useStore } from 'vuex';

import QuestionWithField from '@/shared/components/form/QuestionWithField.vue';
import useApi from '@/shared/composables/useApi';
import useAssets from '@/shared/composables/useAssets';
import useEventBus from '@/shared/composables/useEventBus';
import useTerms from '@/shared/composables/useTerms';

const eventBus = useEventBus();
const { api } = useApi();
const store = useStore();
const { assets } = useAssets();
const $bct = inject('translator');
const { terms } = useTerms();

defineProps({
  questions: {
    type: Array,
    required: true,
  },
  schema: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['change']);

const visible = ref(false);
const attendanceLog = ref(null);
const submitButtonVisible = ref(false);

const role = computed(() => store.state.role);

const canUnsubmit = computed(() => {
  return attendanceLog.value?.submitted_at && role.value === 'specialist';
});

const readonly = computed(() => {
  if (attendanceLog.value?.submitted_at) {
    return true;
  }
  return false;
});

const processing = ref(false);

const getMonthText = (monthValue, year) => {
  const modifiedMonthValue = assets.month_day_options?.find(
    (option) => option.text === monthValue.toString(),
  ).value;
  const monthOption = assets.month_options?.find((option) => option.value === modifiedMonthValue);

  return $bct(monthOption?.text) + ' ' + year;
};

const handleSaveDraft = async () => {
  processing.value = true;
  const updatedAttendanceLog = { ...attendanceLog.value };

  const response = await api.shared.attendanceLog.update(
    attendanceLog.value.id,
    updatedAttendanceLog,
  );

  processing.value = false;

  if (response?.status === 200) {
    eventBus.chime('Saved');
    emit('change', updatedAttendanceLog);
  }
};

const handleSubmit = async () => {
  processing.value = true;
  const updatedAttendanceLog = { ...attendanceLog.value, submitted: true };

  const response = await api.shared.attendanceLog.update(
    attendanceLog.value.id,
    updatedAttendanceLog,
  );

  processing.value = false;

  if (response?.status === 200) {
    eventBus.chime('Submitted');
    emit('change', updatedAttendanceLog);
    close();
  }
};

const handleUnsubmit = async () => {
  processing.value = true;
  const updatedAttendanceLog = { submitted: false };

  const response = await api.shared.attendanceLog.update(
    attendanceLog.value.id,
    updatedAttendanceLog,
  );

  processing.value = false;

  if (response?.status === 200) {
    eventBus.chime('Unsubmitted');
    emit('change', { ...attendanceLog.value, submitted: false });
    close();
  }
};

const open = (newValue, showSubmitButton = false) => {
  attendanceLog.value = newValue;
  submitButtonVisible.value = showSubmitButton;

  visible.value = 'attendanceLog';
};

const close = () => {
  visible.value = false;
};

const isOpen = () => !!visible.value;

defineExpose({
  open,
  close,
  isOpen,
});
</script>

<style scoped>
.attendance-log-editor-bar {
  overflow-y: auto;
  padding-top: 64px;
  position: fixed;
  top: 0;
  z-index: 1000;
  right: 0;
  width: 30%;
}

@media only screen and (max-width: 1200px) {
  .attendance-log-editor-bar {
    overflow-y: auto;
    padding-top: 64px;
    padding-bottom: 72px;
    position: fixed;
    top: 0;
    z-index: 1000;
    right: 0;
    width: 50%;
  }
}

@media only screen and (max-width: 800px) {
  .attendance-log-editor-bar {
    overflow-y: auto;
    padding-top: 64px;
    padding-bottom: 72px;
    position: fixed;
    top: 0;
    z-index: 1000;
    right: 0;
    width: 100%;
  }
}
</style>
